/**
 * Position Type Selector Component
 * Multi-select dropdown with dynamic field opening for customer quote positions
 * Implements reverse engineering supplier quote process
 */

import React, { useState, useEffect } from 'react';
import { CustomerQuotePosition } from '../../../types/unifiedOrderTypes';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  TextField,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  Build as CarbideIcon,
  Category as MaterialIcon,
  Palette as CoatingIcon,
  LocalFireDepartment as HardeningIcon,
  Construction as ToolIcon,
  Close as CloseIcon
} from '@mui/icons-material';

// Position type definitions matching unified-order-types.ts
export type PositionType = 'material' | 'heat_treatment' | 'coating' | 'tool' | 'carbide' | 'no_supplier_quotes';

export interface PositionTypeSelection {
  quantity: number;
  positionNumber: number;
  customerQuantity: number;
  selectedTypes: PositionType[];
  typeConfigurations: {
    carbide?: CarbideData;
    material?: MaterialData;
    coating?: CoatingData;
    heat_treatment?: HeatTreatmentData;
    tool?: ToolData;
    no_supplier_quotes?: {}; // No configuration needed for direct calculation
  };
}

// Type-specific configuration interfaces (from unified-order-types.ts)
export interface CarbideData {
  material_spec: string;          // Required - Material specifications
  dimensional_drawing_path?: string; // Path to uploaded dimensional drawing
}

export interface MaterialData {
  form: string;                    // Round, Flat, Square, etc.
  cross_section?: string;          // Dimensions
  length_mm?: number;              // Length in millimeters
  steel_quality?: string;          // Material grade
  internal_order_reference?: string; // Internal reference
}

export interface CoatingData {
  treatment_type: string;          // PVD, CVD, DLC, etc.
  coating_spec: string;           // Detailed specification
  steel_quality: string;          // Material specification
  weight_kg?: number;             // Weight specification
  internal_order_reference?: string; // Internal reference
  additional_info?: string;        // Multi-line notes
}

export interface HeatTreatmentData {
  treatment_type: string;          // Vacuum-Hardening, Tempering, etc.
  hrc_range?: string;              // Hardness range
  steel_quality?: string;          // Material specification
  weight_kg?: number;              // Weight in kilograms
  internal_order_reference?: string; // Internal reference
  additional_info?: string;        // Multi-line notes
}

export interface ToolData {
  manufacturer_article_number: string; // Required - Primary identifier (e.g., "RT52")
  tool_type: string;                  // Required - Milling Cutter, Drilling Tool, Grinding Disk, etc.
  manufacturer: string;               // Required - ATORN, Sandvik, Kennametal, etc.
  additional_info: string;            // Required - Full description
}

interface PositionTypeSelectorProps {
  position: CustomerQuotePosition;
  selectedTypes: PositionType[];
  onTypeSelectionChange: (types: PositionType[]) => void;
  onConfigurationChange: (type: PositionType, config: any) => void;
  existingTypeSelections?: PositionTypeSelection;
  disabled?: boolean; // Add disabled prop for locking selections
}

// Available position types with metadata (matching unified-order-types.ts)
const POSITION_TYPES = [
  { 
    value: 'no_supplier_quotes' as PositionType, 
    label: 'No Supplier Quotes', 
    icon: <CloseIcon />, 
    color: '#757575',
    description: 'Direct 4DB calculation without supplier quotes'
  },
  { 
    value: 'carbide' as PositionType, 
    label: 'Carbide', 
    icon: <CarbideIcon />, 
    color: '#1976d2',
    description: 'Carbide cutting tools, end mills, drills'
  },
  { 
    value: 'material' as PositionType, 
    label: 'Material', 
    icon: <MaterialIcon />, 
    color: '#388e3c',
    description: 'Raw materials, stock, blanks'
  },
  { 
    value: 'coating' as PositionType, 
    label: 'Coating', 
    icon: <CoatingIcon />, 
    color: '#f57c00',
    description: 'PVD, CVD, other surface treatments'
  },
  { 
    value: 'heat_treatment' as PositionType, 
    label: 'Heat Treatment', 
    icon: <HardeningIcon />, 
    color: '#d32f2f',
    description: 'Vacuum hardening, tempering, etc.'
  },
  { 
    value: 'tool' as PositionType, 
    label: 'Tool', 
    icon: <ToolIcon />, 
    color: '#7b1fa2',
    description: 'Custom tool manufacturing, grinding'
  }
];

export const PositionTypeSelector: React.FC<PositionTypeSelectorProps> = ({
  position,
  selectedTypes,
  onTypeSelectionChange,
  onConfigurationChange,
  existingTypeSelections,
  disabled = false
}) => {
  const [configurations, setConfigurations] = useState<any>({});
  const [expandedTypes, setExpandedTypes] = useState<Set<PositionType>>(new Set());
  const [uploadedFiles, setUploadedFiles] = useState<Map<string, File[]>>(new Map());

  // Initialize configurations from existing data
  useEffect(() => {
    console.log('🔍 [FILE RESTORATION DEBUG] useEffect triggered', {
      existingTypeSelections,
      selectedTypes,
      hasTypeConfigurations: !!existingTypeSelections?.typeConfigurations
    });

    if (existingTypeSelections?.typeConfigurations) {
      console.log('🔍 [FILE RESTORATION DEBUG] Processing type configurations', existingTypeSelections.typeConfigurations);
      
      setConfigurations(existingTypeSelections.typeConfigurations);
      // Auto-expand selected types
      setExpandedTypes(new Set(selectedTypes));
      
      // Restore uploaded files state from position files array
      const restoredFiles = new Map<string, File[]>();
      
      // Check if position has files from the database
      if (position.files && position.files.length > 0) {
        console.log('🔍 [FILE RESTORATION DEBUG] Found files in position:', position.files);
        
        // Create pseudo-File objects for each uploaded file
        const pseudoFiles = position.files.map(fileData => {
          console.log('🔍 [FILE RESTORATION DEBUG] Creating pseudo-file from DB', {
            filename: fileData.filename,
            original_filename: fileData.original_filename,
            file_path: fileData.file_path
          });
          
          const pseudoFile = new File([''], fileData.original_filename, { 
            type: fileData.mime_type || 'application/pdf' 
          });
          // Mark this as a restored file so we can distinguish it
          (pseudoFile as any).isRestoredFromDB = true;
          (pseudoFile as any).dbPath = fileData.file_path;
          (pseudoFile as any).dbId = fileData.id;
          (pseudoFile as any).dbFilename = fileData.filename;
          
          return pseudoFile;
        });
        
        // For now, associate all files with carbide type (this can be enhanced later)
        if (pseudoFiles.length > 0) {
          restoredFiles.set('carbide', pseudoFiles);
          console.log('🔍 [FILE RESTORATION DEBUG] Added files to carbide type:', pseudoFiles);
        }
      }
      
      // Also check legacy dimensional_drawing_path for backward compatibility
      Object.entries(existingTypeSelections.typeConfigurations).forEach(([type, config]: [string, any]) => {
        console.log('🔍 [FILE RESTORATION DEBUG] Processing legacy type', { type, config, hasPath: !!config?.dimensional_drawing_path });
        
        if (config?.dimensional_drawing_path && !restoredFiles.has(type as PositionType)) {
          // Create a pseudo-File object to represent the uploaded file
          const fileName = config.dimensional_drawing_path;
          console.log('🔍 [FILE RESTORATION DEBUG] Creating legacy pseudo-file', { fileName, type });
          
          const pseudoFile = new File([''], fileName, { type: 'application/pdf' });
          // Mark this as a restored file so we can distinguish it
          (pseudoFile as any).isRestoredFromDB = true;
          (pseudoFile as any).dbPath = fileName;
          restoredFiles.set(type as PositionType, [pseudoFile]);
          
          console.log('🔍 [FILE RESTORATION DEBUG] Added legacy pseudo-file to restoredFiles', {
            type,
            fileName,
            pseudoFileSize: pseudoFile.size,
            pseudoFileName: pseudoFile.name
          });
        }
      });
      
      console.log('🔍 [FILE RESTORATION DEBUG] Setting uploaded files', {
        restoredFilesSize: restoredFiles.size,
        restoredFilesKeys: Array.from(restoredFiles.keys()),
        restoredFilesEntries: Array.from(restoredFiles.entries()).map(([key, files]) => ({
          key,
          fileCount: files.length,
          fileNames: files.map(f => f.name)
        }))
      });
      
      setUploadedFiles(restoredFiles);
    } else {
      console.log('🔍 [FILE RESTORATION DEBUG] No type configurations found, clearing files');
      setUploadedFiles(new Map());
    }
  }, [existingTypeSelections, selectedTypes]);

  // Handle type selection changes
  const handleTypeChange = (event: any) => {
    let newSelectedTypes = event.target.value as PositionType[];
    
    // Handle "No supplier quotes" as exclusive option
    if (newSelectedTypes.includes('no_supplier_quotes')) {
      if (newSelectedTypes.length > 1) {
        // If "No supplier quotes" is selected with others, make it exclusive
        newSelectedTypes = ['no_supplier_quotes'];
      }
    } else {
      // If other types are selected, remove "No supplier quotes"
      newSelectedTypes = newSelectedTypes.filter(type => type !== 'no_supplier_quotes');
    }
    
    onTypeSelectionChange(newSelectedTypes);
    
    // Auto-expand newly selected types (but not "no_supplier_quotes")
    const newTypes = newSelectedTypes.filter(type => 
      !selectedTypes.includes(type) && type !== 'no_supplier_quotes'
    );
    if (newTypes.length > 0) {
      setExpandedTypes(prev => {
        const newSet = new Set(prev);
        newTypes.forEach(type => newSet.add(type));
        return newSet;
      });
    }
    
    // Remove configurations for deselected types
    const removedTypes = selectedTypes.filter(type => !newSelectedTypes.includes(type));
    if (removedTypes.length > 0) {
      const updatedConfigurations = { ...configurations };
      removedTypes.forEach(type => {
        delete updatedConfigurations[type];
      });
      setConfigurations(updatedConfigurations);
      
      // Also remove uploaded files for deselected types
      const newUploadedFiles = new Map(uploadedFiles);
      removedTypes.forEach(type => {
        newUploadedFiles.delete(type);
      });
      setUploadedFiles(newUploadedFiles);
    }
  };

  // Handle removing a specific type
  const handleRemoveType = (typeToRemove: PositionType) => {
    console.log('🐛 DEBUG: handleRemoveType called', { typeToRemove, selectedTypes });
    const newSelectedTypes = selectedTypes.filter(type => type !== typeToRemove);
    console.log('🐛 DEBUG: New selected types', newSelectedTypes);
    onTypeSelectionChange(newSelectedTypes);
    
    // Remove configuration and files
    const updatedConfigurations = { ...configurations };
    delete updatedConfigurations[typeToRemove];
    setConfigurations(updatedConfigurations);
    
    const newUploadedFiles = new Map(uploadedFiles);
    newUploadedFiles.delete(typeToRemove);
    setUploadedFiles(newUploadedFiles);
    
    // Remove from expanded set
    const newExpanded = new Set(expandedTypes);
    newExpanded.delete(typeToRemove);
    setExpandedTypes(newExpanded);
  };

  // Handle configuration changes for specific types
  const handleConfigurationChange = (type: PositionType, field: string, value: any) => {
    const updatedConfig = {
      ...configurations[type],
      [field]: value
    };
    
    const newConfigurations = {
      ...configurations,
      [type]: updatedConfig
    };
    
    setConfigurations(newConfigurations);
    onConfigurationChange(type, updatedConfig);
  };

  // Handle file uploads
  const handleFileUpload = async (type: PositionType, files: FileList | null) => {
    console.log('🐛 DEBUG: handleFileUpload called', { type, files: files ? Array.from(files) : null });
    
    if (!files || files.length === 0) return;
    
    const fileArray = Array.from(files);
    
    try {
      // Upload files to server
      const formData = new FormData();
      fileArray.forEach(file => {
        formData.append('files', file);
      });
      formData.append('description', `${type} specifications for position ${position.position_number}`);
      
      console.log('🐛 DEBUG: Uploading files to:', `/api/v3/cases/positions/${position.id}/files`);
      
      const response = await fetch(`/api/v3/cases/positions/${position.id}/files`, {
        method: 'POST',
        body: formData
      });
      
      console.log('🐛 DEBUG: Upload response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🐛 DEBUG: Upload failed:', errorText);
        throw new Error('Failed to upload files');
      }
      
      const result = await response.json();
      console.log('🐛 DEBUG: Upload result:', result);
      
      // Update local state
      const newUploadedFiles = new Map(uploadedFiles);
      newUploadedFiles.set(type, fileArray);
      setUploadedFiles(newUploadedFiles);
      
      // For carbide, update the dimensional_drawing_path
      if (type === 'carbide' && fileArray.length > 0) {
        // Use the full path format: unified/filename (same as working system)
        const fullPath = `unified/${result.data?.[0]?.filename || fileArray[0].name}`;
        handleConfigurationChange(type, 'dimensional_drawing_path', fullPath);
      }
      
      console.log('🐛 DEBUG: Files uploaded successfully:', result);
    } catch (error) {
      console.error('🐛 DEBUG: Error uploading files:', error);
      // Still update local state for display purposes
      const newUploadedFiles = new Map(uploadedFiles);
      newUploadedFiles.set(type, fileArray);
      setUploadedFiles(newUploadedFiles);
      
      // For carbide, update with filename  
      if (type === 'carbide' && fileArray.length > 0) {
        // Use consistent path format even in fallback case
        const fullPath = `unified/${fileArray[0].name}`;
        handleConfigurationChange(type, 'dimensional_drawing_path', fullPath);
      }
    }
  };

  // Toggle accordion expansion
  const toggleExpanded = (type: PositionType) => {
    const newExpanded = new Set(expandedTypes);
    if (newExpanded.has(type)) {
      newExpanded.delete(type);
    } else {
      newExpanded.add(type);
    }
    setExpandedTypes(newExpanded);
  };

  // Get type metadata
  const getTypeMetadata = (type: PositionType) => {
    return POSITION_TYPES.find(t => t.value === type);
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Position {position.position_number}: {position.part_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Quantity: {position.quantity} | Part Number: {position.part_number || 'N/A'}
            </Typography>
          </Box>
          <Chip 
            label={`${selectedTypes.length} type${selectedTypes.length !== 1 ? 's' : ''} selected`}
            color={selectedTypes.length > 0 ? 'primary' : 'default'}
            variant={selectedTypes.length > 0 ? 'filled' : 'outlined'}
          />
        </Box>

        {/* Type Selection Dropdown */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Select Position Types</InputLabel>
          <Select
            multiple
            value={selectedTypes}
            onChange={handleTypeChange}
            disabled={disabled}
            input={<OutlinedInput label="Select Position Types" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const metadata = getTypeMetadata(value);
                  return (
                    <Chip
                      key={value}
                      label={metadata?.label}
                      size="small"
                      icon={metadata?.icon}
                      onDelete={disabled ? undefined : (e) => {
                        console.log('🐛 DEBUG: Chip onDelete clicked', { value, target: e.target });
                        e.stopPropagation();
                        e.preventDefault();
                        console.log('🐛 DEBUG: About to call handleRemoveType', value);
                        handleRemoveType(value);
                      }}
                      deleteIcon={<CloseIcon sx={{ fontSize: '18px' }} />}
                      sx={{ 
                        backgroundColor: metadata?.color, 
                        color: 'white',
                        '& .MuiChip-deleteIcon': {
                          color: 'white',
                          '&:hover': {
                            color: 'rgba(255, 255, 255, 0.8)'
                          }
                        }
                      }}
                    />
                  );
                })}
              </Box>
            )}
          >
            {POSITION_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                <Box display="flex" alignItems="center" gap={1}>
                  {type.icon}
                  <Box>
                    <Typography variant="body2">{type.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {type.description}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Configuration Sections for Selected Types */}
        {selectedTypes.length > 0 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Configure the selected types below. Each type will be aggregated into separate supplier quotes.
            </Alert>

            {selectedTypes
              .filter(type => type !== 'no_supplier_quotes') // Don't show accordion for "no supplier quotes"
              .map((type) => {
              const metadata = getTypeMetadata(type);
              const isExpanded = expandedTypes.has(type);
              
              return (
                <Accordion 
                  key={type}
                  expanded={isExpanded}
                  onChange={() => toggleExpanded(type)}
                  sx={{ mb: 1 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {metadata?.icon}
                      <Typography variant="subtitle2">
                        {metadata?.label} Configuration
                      </Typography>
                      <Chip 
                        size="small" 
                        label={configurations[type] ? 'Configured' : 'Needs Configuration'}
                        color={configurations[type] ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {type === 'carbide' && (
                      <CarbideConfigurationForm 
                        config={configurations[type] || {}}
                        onChange={(field, value) => handleConfigurationChange(type, field, value)}
                      />
                    )}
                    {type === 'material' && (
                      <MaterialConfigurationForm 
                        config={configurations[type] || {}}
                        onChange={(field, value) => handleConfigurationChange(type, field, value)}
                      />
                    )}
                    {type === 'coating' && (
                      <CoatingConfigurationForm 
                        config={configurations[type] || {}}
                        onChange={(field, value) => handleConfigurationChange(type, field, value)}
                      />
                    )}
                    {type === 'heat_treatment' && (
                      <HeatTreatmentConfigurationForm 
                        config={configurations[type] || {}}
                        onChange={(field, value) => handleConfigurationChange(type, field, value)}
                      />
                    )}
                    {type === 'tool' && (
                      <ToolConfigurationForm 
                        config={configurations[type] || {}}
                        onChange={(field, value) => handleConfigurationChange(type, field, value)}
                      />
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Carbide Configuration Form
const CarbideConfigurationForm: React.FC<{
  config: Partial<CarbideData>;
  onChange: (field: string, value: any) => void;
}> = ({ config, onChange }) => {
  console.log('🔍 [CARBIDE FORM DEBUG] Component rendered', {
    config
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Material Specification"
          value={config.material_spec || ''}
          onChange={(e) => onChange('material_spec', e.target.value)}
        placeholder="e.g., Carbide grade specifications, cutting parameters"
        required
        multiline
        rows={2}
      />
    </Grid>
    <Grid item xs={12}>
      <TextField
        fullWidth
        label="Dimensional Drawing Path"
        value={config.dimensional_drawing_path || ''}
        onChange={(e) => onChange('dimensional_drawing_path', e.target.value)}
        placeholder="Path to dimensional drawing (set via file upload in Quote Manager)"
        disabled
        helperText="Files are uploaded via the Quote Aggregation Manager"
      />
    </Grid>
  </Grid>
  );
};

// Material Configuration Form
const MaterialConfigurationForm: React.FC<{
  config: Partial<MaterialData>;
  onChange: (field: string, value: any) => void;
}> = ({ config, onChange }) => (
  <Grid container spacing={2}>
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        label="Form"
        value={config.form || ''}
        onChange={(e) => onChange('form', e.target.value)}
        placeholder="e.g., Round, Flat, Square"
        required
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        label="Cross Section"
        value={config.cross_section || ''}
        onChange={(e) => onChange('cross_section', e.target.value)}
        placeholder="e.g., Ø50mm, 100x50mm"
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        label="Length (mm)"
        type="number"
        value={config.length_mm || ''}
        onChange={(e) => onChange('length_mm', parseFloat(e.target.value))}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        label="Steel Quality"
        value={config.steel_quality || ''}
        onChange={(e) => onChange('steel_quality', e.target.value)}
        placeholder="e.g., 1.4404, 42CrMo4"
      />
    </Grid>
    <Grid item xs={12}>
      <TextField
        fullWidth
        label="Internal Order Reference"
        value={config.internal_order_reference || ''}
        onChange={(e) => onChange('internal_order_reference', e.target.value)}
        placeholder="Internal reference number"
      />
    </Grid>
    <Grid item xs={12}>
      <TextField
        fullWidth
        label="Material Specifications Notes"
        value={config.internal_order_reference || ''}
        onChange={(e) => onChange('internal_order_reference', e.target.value)}
        placeholder="Material specifications and drawing details (files uploaded via Quote Manager)"
        helperText="Files are uploaded via the Quote Aggregation Manager"
        multiline
        rows={2}
      />
    </Grid>
  </Grid>
);

// Coating Configuration Form
const CoatingConfigurationForm: React.FC<{
  config: Partial<CoatingData>;
  onChange: (field: string, value: any) => void;
}> = ({ config, onChange }) => (
  <Grid container spacing={2}>
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        label="Treatment Type"
        value={config.treatment_type || ''}
        onChange={(e) => onChange('treatment_type', e.target.value)}
        placeholder="e.g., PVD TiAlN, CVD Al2O3"
        required
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        label="Coating Specification"
        value={config.coating_spec || ''}
        onChange={(e) => onChange('coating_spec', e.target.value)}
        placeholder="Detailed coating specification"
        required
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        label="Steel Quality"
        value={config.steel_quality || ''}
        onChange={(e) => onChange('steel_quality', e.target.value)}
        placeholder="Material specification"
        required
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        label="Weight (kg)"
        type="number"
        value={config.weight_kg || ''}
        onChange={(e) => onChange('weight_kg', parseFloat(e.target.value))}
      />
    </Grid>
    <Grid item xs={12}>
      <TextField
        fullWidth
        label="Internal Order Reference"
        value={config.internal_order_reference || ''}
        onChange={(e) => onChange('internal_order_reference', e.target.value)}
        placeholder="Internal reference number"
      />
    </Grid>
    <Grid item xs={12}>
      <TextField
        fullWidth
        label="Additional Information"
        value={config.additional_info || ''}
        onChange={(e) => onChange('additional_info', e.target.value)}
        placeholder="Multi-line notes"
        multiline
        rows={3}
      />
    </Grid>

  </Grid>
);

// Heat Treatment Configuration Form
const HeatTreatmentConfigurationForm: React.FC<{
  config: Partial<HeatTreatmentData>;
  onChange: (field: string, value: any) => void;
}> = ({ config, onChange }) => (
  <Grid container spacing={2}>
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        label="Treatment Type"
        value={config.treatment_type || ''}
        onChange={(e) => onChange('treatment_type', e.target.value)}
        placeholder="e.g., Vacuum-Hardening, Tempering"
        required
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        label="HRC Range"
        value={config.hrc_range || ''}
        onChange={(e) => onChange('hrc_range', e.target.value)}
        placeholder="e.g., 58-62 HRC"
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        label="Steel Quality"
        value={config.steel_quality || ''}
        onChange={(e) => onChange('steel_quality', e.target.value)}
        placeholder="Material specification"
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        label="Weight (kg)"
        type="number"
        value={config.weight_kg || ''}
        onChange={(e) => onChange('weight_kg', parseFloat(e.target.value))}
      />
    </Grid>
    <Grid item xs={12}>
      <TextField
        fullWidth
        label="Internal Order Reference"
        value={config.internal_order_reference || ''}
        onChange={(e) => onChange('internal_order_reference', e.target.value)}
        placeholder="Internal reference number"
      />
    </Grid>
    <Grid item xs={12}>
      <TextField
        fullWidth
        label="Additional Information"
        value={config.additional_info || ''}
        onChange={(e) => onChange('additional_info', e.target.value)}
        placeholder="Multi-line notes"
        multiline
        rows={3}
      />
    </Grid>

  </Grid>
);

// Tool Configuration Form
const ToolConfigurationForm: React.FC<{
  config: Partial<ToolData>;
  onChange: (field: string, value: any) => void;
}> = ({ config, onChange }) => (
  <Grid container spacing={2}>
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        label="Manufacturer Article Number"
        value={config.manufacturer_article_number || ''}
        onChange={(e) => onChange('manufacturer_article_number', e.target.value)}
        placeholder="e.g., RT52"
        required
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        label="Tool Type"
        value={config.tool_type || ''}
        onChange={(e) => onChange('tool_type', e.target.value)}
        placeholder="e.g., Milling Cutter, Drilling Tool"
        required
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        label="Manufacturer"
        value={config.manufacturer || ''}
        onChange={(e) => onChange('manufacturer', e.target.value)}
        placeholder="e.g., ATORN, Sandvik, Kennametal"
        required
      />
    </Grid>
    <Grid item xs={12}>
      <TextField
        fullWidth
        label="Additional Information"
        value={config.additional_info || ''}
        onChange={(e) => onChange('additional_info', e.target.value)}
        placeholder="Full description of the tool"
        required
        multiline
        rows={3}
      />
    </Grid>

  </Grid>
);

export default PositionTypeSelector;
